import { createFileRoute } from '@tanstack/react-router';

import { Donut } from '~/components/analytics/Donut.js';
import { FiltrosBar } from '~/components/analytics/FiltrosBar.js';
import { KpiCard } from '~/components/analytics/KpiCard.js';
import { LineChart } from '~/components/analytics/LineChart.js';
import { TopList } from '~/components/analytics/TopList.js';
import { useBreakdown, useKpis, useTimeseries } from '~/features/analytics/hooks.js';
import { analyticsSearchSchema, searchToPeriod } from '~/features/analytics/useFiltros.js';
import { downloadCsv, n } from '~/lib/csv.js';

export const Route = createFileRoute('/_auth/analytics/')({
  validateSearch: (search) => analyticsSearchSchema.parse(search),
  component: AnalyticsOverview,
});

function formatNumber(v: number): string {
  if (v >= 10_000) return `${(v / 1000).toFixed(1).replace('.0', '')}k`;
  return n(v);
}

function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function AnalyticsOverview() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const period = searchToPeriod(search);

  const kpis = useKpis(period);
  const ts = useTimeseries(period);
  const topPages = useBreakdown(period, 'page', 10);
  const topSources = useBreakdown(period, 'source', 10);
  const devices = useBreakdown(period, 'device', 5);

  function setRange(range: '7d' | '30d' | '90d') {
    void navigate({ search: { ...search, range, from: undefined, to: undefined } });
  }
  function toggleComparar() {
    void navigate({ search: { ...search, comparar: !period.comparar } });
  }

  function handleExport() {
    if (!topPages.data) return;
    downloadCsv(`analytics-paginas-${period.range}`, topPages.data.items, [
      { key: 'name', label: 'Página' },
      { key: 'visitors', label: 'Visitantes' },
      { key: 'pageviews', label: 'Pageviews' },
    ]);
  }

  const deviceData = (devices.data?.items ?? []).map((i) => ({
    name: i.name,
    value: i.visitors,
  }));

  return (
    <div className="analytics-page">
      <FiltrosBar
        period={period}
        onRangeChange={setRange}
        onToggleComparar={toggleComparar}
        onExportCsv={handleExport}
      />

      <section aria-label="KPIs do período" className="analytics-kpi-grid">
        <KpiCard
          label="Visitantes únicos"
          value={kpis.data ? formatNumber(kpis.data.current.visitors) : '—'}
          delta={kpis.data?.delta?.visitors ?? null}
        />
        <KpiCard
          label="Pageviews"
          value={kpis.data ? formatNumber(kpis.data.current.pageviews) : '—'}
          delta={kpis.data?.delta?.pageviews ?? null}
        />
        <KpiCard
          label="Taxa de rejeição"
          value={kpis.data ? `${(kpis.data.current.bounceRate * 100).toFixed(1)}%` : '—'}
          delta={kpis.data?.delta?.bounceRate ?? null}
          inverse
        />
        <KpiCard
          label="Duração média"
          value={kpis.data ? formatDuration(kpis.data.current.avgDurationSec) : '—'}
          delta={kpis.data?.delta?.avgDurationSec ?? null}
        />
      </section>

      <section className="analytics-block" aria-label="Visitantes ao longo do tempo">
        <header className="analytics-block__head">
          <h2 className="analytics-block__title">Visitantes diários</h2>
        </header>
        {ts.data ? (
          <LineChart
            buckets={ts.data.buckets}
            previousBuckets={ts.data.previousBuckets}
            metric="visitors"
          />
        ) : (
          <p className="analytics-empty">Carregando...</p>
        )}
      </section>

      <section className="analytics-grid-2" aria-label="Topo de páginas e fontes">
        <TopList
          title="Top páginas"
          items={topPages.data?.items ?? []}
          renderName={(item) => <code className="analytics-top__path">{item.name}</code>}
        />
        <TopList title="Top fontes" items={topSources.data?.items ?? []} />
      </section>

      {deviceData.length > 0 && (
        <section className="analytics-block" aria-label="Distribuição por dispositivo">
          <header className="analytics-block__head">
            <h2 className="analytics-block__title">Dispositivos</h2>
          </header>
          <Donut data={deviceData} />
        </section>
      )}
    </div>
  );
}
